import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
    Calendar, Car, DollarSign, Star, Heart, Settings, User
} from 'lucide-react'
import { useAuthStore, useLanguageStore, useCurrencyStore } from '../../lib/store'
import { formatCurrency } from '../../lib/utils'
import { walletApi, bookingsApi } from '../../lib/api'

export default function DashboardPage() {
    useTranslation()
    const { user } = useAuthStore()
    const { language } = useLanguageStore()
    useCurrencyStore()

    const [walletBalance, setWalletBalance] = useState(0)
    const [recentBookings, setRecentBookings] = useState<any[]>([])

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch wallet balance
                const walletResponse = await walletApi.getBalance()
                if (walletResponse.data?.balance !== undefined) {
                    setWalletBalance(walletResponse.data.balance)
                }

                // Fetch recent bookings
                const bookingsResponse = await bookingsApi.list({ limit: 5 })
                if (bookingsResponse.data?.bookings) {
                    setRecentBookings(bookingsResponse.data.bookings)
                }
            } catch (error) {
                console.error('Failed to fetch dashboard data:', error)
            }
        }
        fetchData()
    }, [])

    // Stats - wallet from API, others mock for now
    const stats = {
        activeBookings: recentBookings.filter((b: any) => ['pending', 'confirmed', 'in_progress'].includes(b.status)).length,
        completedBookings: recentBookings.filter((b: any) => b.status === 'completed').length,
        savedVehicles: 3,
        walletBalance: walletBalance,
    }

    const savedVehicles = [
        { id: 1, title: 'Yamaha Exciter 155', dailyRate: 250000, rating: 4.6 },
        { id: 2, title: 'Honda SH 150i', dailyRate: 300000, rating: 4.9 },
        { id: 3, title: 'Toyota Fortuner 2023', dailyRate: 1500000, rating: 4.8 },
    ]

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="container mx-auto px-4">
                {/* Welcome */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold">
                        {language === 'en' ? `Hello, ${user?.fullName || 'Guest'}!` : `Xin chào, ${user?.fullName || 'Khách'}!`}
                    </h1>
                    <p className="text-gray-600">{language === 'en' ? 'Welcome to your dashboard' : 'Chào mừng đến bảng điều khiển'}</p>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    <Link to="/dashboard/bookings?status=active" className="card hover:shadow-lg transition">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                <Calendar className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                                <p className="text-gray-600 text-sm">{language === 'en' ? 'Active' : 'Đang thuê'}</p>
                                <p className="text-2xl font-bold text-blue-600">{stats.activeBookings}</p>
                            </div>
                        </div>
                    </Link>

                    <Link to="/dashboard/bookings" className="card hover:shadow-lg transition">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                                <Car className="w-5 h-5 text-green-600" />
                            </div>
                            <div>
                                <p className="text-gray-600 text-sm">{language === 'en' ? 'Completed' : 'Hoàn thành'}</p>
                                <p className="text-2xl font-bold">{stats.completedBookings}</p>
                            </div>
                        </div>
                    </Link>

                    <Link to="/dashboard/saved" className="card hover:shadow-lg transition">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                                <Heart className="w-5 h-5 text-red-600" />
                            </div>
                            <div>
                                <p className="text-gray-600 text-sm">{language === 'en' ? 'Saved' : 'Đã lưu'}</p>
                                <p className="text-2xl font-bold">{stats.savedVehicles}</p>
                            </div>
                        </div>
                    </Link>

                    <Link to="/dashboard/wallet" className="card hover:shadow-lg transition">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                                <DollarSign className="w-5 h-5 text-purple-600" />
                            </div>
                            <div>
                                <p className="text-gray-600 text-sm">{language === 'en' ? 'Wallet' : 'Ví'}</p>
                                <p className="text-lg font-bold">{formatCurrency(stats.walletBalance, 'VND')}</p>
                            </div>
                        </div>
                    </Link>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Recent Bookings */}
                    <div className="card">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold">{language === 'en' ? 'Recent Bookings' : 'Đơn Gần Đây'}</h2>
                            <Link to="/dashboard/bookings" className="text-primary-600 text-sm font-medium">
                                {language === 'en' ? 'View all' : 'Xem tất cả'}
                            </Link>
                        </div>
                        <div className="space-y-3">
                            {recentBookings.length === 0 ? (
                                <p className="text-gray-500 text-center py-4">{language === 'en' ? 'No bookings yet' : 'Chưa có đơn đặt'}</p>
                            ) : recentBookings.map((booking: any) => (
                                <Link key={booking.id} to={`/dashboard/bookings/${booking.id}`} className="block p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="font-medium">{language === 'en' ? booking.vehicle_title_en : (booking.vehicle_title_vi || booking.vehicle_title_en) || 'Vehicle'}</p>
                                            <p className="text-sm text-gray-500">{booking.start_date} → {booking.end_date}</p>
                                        </div>
                                        <div className="text-right">
                                            <p className="font-bold">{formatCurrency(booking.total_price || 0, 'VND')}</p>
                                            <span className={`badge ${booking.status === 'confirmed' ? 'badge-success' : 'badge-gray'}`}>
                                                {booking.status}
                                            </span>
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>

                    {/* Saved Vehicles */}
                    <div className="card">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold">{language === 'en' ? 'Saved Vehicles' : 'Xe Đã Lưu'}</h2>
                            <Link to="/dashboard/saved" className="text-primary-600 text-sm font-medium">
                                {language === 'en' ? 'View all' : 'Xem tất cả'}
                            </Link>
                        </div>
                        <div className="space-y-3">
                            {savedVehicles.map((vehicle) => (
                                <Link key={vehicle.id} to={`/vehicles/${vehicle.id}`} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                                    <div className="w-16 h-12 bg-gray-200 rounded flex items-center justify-center">🏍️</div>
                                    <div className="flex-1">
                                        <p className="font-medium">{vehicle.title}</p>
                                        <div className="flex items-center gap-2 text-sm">
                                            <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                                            <span>{vehicle.rating}</span>
                                        </div>
                                    </div>
                                    <p className="font-bold text-primary-600">{formatCurrency(vehicle.dailyRate, 'VND')}/{language === 'en' ? 'day' : 'ngày'}</p>
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Quick Links */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
                    <Link to="/vehicles" className="card hover:shadow-lg transition text-center py-6">
                        <Car className="w-8 h-8 mx-auto mb-2 text-primary-600" />
                        <p className="font-medium">{language === 'en' ? 'Browse Vehicles' : 'Tìm xe'}</p>
                    </Link>
                    <Link to="/tours" className="card hover:shadow-lg transition text-center py-6">
                        <Calendar className="w-8 h-8 mx-auto mb-2 text-primary-600" />
                        <p className="font-medium">{language === 'en' ? 'Book Tours' : 'Đặt tour'}</p>
                    </Link>
                    <Link to="/dashboard/profile" className="card hover:shadow-lg transition text-center py-6">
                        <User className="w-8 h-8 mx-auto mb-2 text-primary-600" />
                        <p className="font-medium">{language === 'en' ? 'My Profile' : 'Hồ sơ'}</p>
                    </Link>
                    <Link to="/dashboard/settings" className="card hover:shadow-lg transition text-center py-6">
                        <Settings className="w-8 h-8 mx-auto mb-2 text-primary-600" />
                        <p className="font-medium">{language === 'en' ? 'Settings' : 'Cài đặt'}</p>
                    </Link>
                </div>
            </div>
        </div>
    )
}
